import React, { useEffect, useState } from 'react';
import {
	AppBar,
	Box,
	Toolbar,
	Button,
	useTheme as useMuiTheme,
	useMediaQuery,
	IconButton,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

const navLinks = [
	{ label: 'Home', to: '/' },
	{ label: 'About', to: '/about' },
	{ label: 'Careers', to: '/careers' },
	{ label: 'Login', to: '/login' },
	{ label: 'Register', to: '/register' },
];

const Navbar = () => {
	const location = useLocation();
	const [scrolled, setScrolled] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const muiTheme = useMuiTheme();
	const { mode } = useTheme();
	const isHome = true;
	const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

	useEffect(() => {
		if (!isHome) return;
		const handleScroll = () => {
			setScrolled(window.scrollY > 40);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [isHome]);

	// Dynamic logo based on theme and scroll state
	const getLogoSrc = () => {
		if (mode === 'light') {
			return '/logo.svg';
		}
		return '/logo-dark.svg'; // Dark theme always uses dark logo
	};

	// Dynamic background color based on theme and scroll state
	const getBackgroundColor = () => {
		if (isHome && !scrolled) {
			return 'transparent';
		}
		return mode === 'dark' 
			? 'rgba(20,20,20,0.95)' 
			: 'rgba(250, 251, 252, 0.98)'; // Improved light mode with better opacity
	};

	// Dynamic text color based on theme
	const getTextColor = () => {
		if (isHome && !scrolled) {
			return mode === 'dark' ? 'white' : 'black';
		}
		return mode === 'dark' ? 'white' : muiTheme.palette.text.primary;
	};

	// Get border color for better separation
	const getBorderColor = () => {
		if (isHome && !scrolled) {
			return 'transparent';
		}
		return mode === 'dark' ? 'rgba(51, 51, 51, 0.3)' : 'rgba(225, 229, 233, 0.8)';
	};

	// Get box shadow for better depth
	const getBoxShadow = () => {
		if (isHome && !scrolled) {
			return 'none';
		}
		return mode === 'dark' 
			? '0 4px 20px rgba(0, 0, 0, 0.3)' 
			: '0 4px 20px rgba(0, 0, 0, 0.08)';
	};

	return (
		<AppBar
			position="fixed"
			elevation={scrolled || !isHome ? 4 : 0}
			sx={{
				transition: 'all 0.3s ease',
                border: 'none',
				background: getBackgroundColor(),
				boxShadow: getBoxShadow(),
				borderBottom: scrolled ? '1px solid' : 'none',
				borderColor: getBorderColor(),
				height: isHome && !scrolled ? 96 : 64,
				justifyContent: 'center',
				backdropFilter: scrolled || !isHome ? 'blur(15px)' : 'none',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: mode === 'light' && (scrolled || !isHome) 
						? 'linear-gradient(180deg, rgba(250, 251, 252, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)'
						: 'none',
					zIndex: -1,
				},
			}}
		>
			<Toolbar sx={{ minHeight: '0!important', height: 1, px: { xs: 2, md: 6 } }}>
				<Box
					component={RouterLink}
					to="/"
					sx={{ display: 'flex', alignItems: 'center', mr: 4, textDecoration: 'none', flexShrink: 0 }}
				>
					<Box
						component="img"
						src={getLogoSrc()}
						alt="Logo"
						sx={{ height: isHome && !scrolled ? 45 : 35, transition: 'height 0.3s', width: { xs: 150, lg: 180 } }}
					/>
				</Box>
				<Box sx={{ flexGrow: 1 }} />
				{isMobile ? (
					<>
						<ThemeToggle color="inherit" size="medium" />
						<IconButton
							edge="end"
							color="inherit"
							aria-label="menu"
							onClick={() => setDrawerOpen(true)}
							sx={{ color: getTextColor() }}
						>
							<MenuIcon fontSize="large" />
						</IconButton>
						<Drawer
							anchor="left"
							open={drawerOpen}
							onClose={() => setDrawerOpen(false)}
							PaperProps={{
								sx: { 
									bgcolor: mode === 'dark' ? 'rgba(20,20,20,0.98)' : 'rgba(255,255,255,0.98)',
									width: '50vw',
									backdropFilter: 'blur(10px)',
								},
							}}
						>
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
								<IconButton 
									onClick={() => setDrawerOpen(false)}
									sx={{ color: getTextColor() }}
								>
									<CloseIcon />
								</IconButton>
							</Box>
							<AnimatePresence>
								<motion.div
									initial={{ x: 100, opacity: 0 }}
									animate={{ x: 0, opacity: 1 }}
									exit={{ x: 100, opacity: 0 }}
									transition={{ duration: 0.3 }}
								>
									<List>
										{navLinks.map((link) => (
											<ListItem key={link.to} disablePadding>
												<ListItemButton
													component={RouterLink}
													to={link.to}
													onClick={() => setDrawerOpen(false)}
													sx={{
														color: location.pathname === link.to 
															? muiTheme.palette.secondary.main 
															: getTextColor(),
														fontWeight: location.pathname === link.to ? 700 : 400,
														fontSize: 18,
														px: 3,
														py: 2,
														borderRight: location.pathname === link.to ? `4px solid ${muiTheme.palette.secondary.main}` : 'none',
														borderLeft: 'none',
													}}
												>
													<ListItemText primary={link.label} />
												</ListItemButton>
											</ListItem>
										))}
									</List>
								</motion.div>
							</AnimatePresence>
						</Drawer>
					</>
				) : (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						{navLinks.map((link) => (
							<Button
								key={link.to}
								component={RouterLink}
								to={link.to}
								sx={{
									color: location.pathname === link.to 
										? muiTheme.palette.secondary.main 
										: getTextColor(),
									fontWeight: location.pathname === link.to ? 700 : 400,
									borderBottom: location.pathname === link.to ? `2px solid ${muiTheme.palette.secondary.main}` : 'none',
									borderRadius: 0,
									px: 2,
									fontSize: { xs: 12, md: 14, lg: 16 },
									background: 'none',
									'&:hover': {
										color: muiTheme.palette.secondary.main,
										background: 'none',
									},
								}}
							>
								{link.label}
							</Button>
						))}
						<ThemeToggle color="inherit" size="medium" />
					</Box>
				)}
			</Toolbar>
		</AppBar>
	);
};

export default Navbar; 